/**
 * Permission codes the CRM screens declare an interest in.
 *
 * They are the dot-strings `internal/crm/permissions.go` registers with the RBAC
 * catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once here
 * means a renamed permission is a compile error rather than a silently invisible
 * menu item.
 *
 * Two of these deserve a note, because the screens treat them as genuinely
 * different decisions rather than as degrees of the same one:
 *
 *  - `leadMerge` is its own permission on the server and its own gate here. A
 *    merge retires one person's record into another's and this app offers no way
 *    back; reading a dedupe list is not the same act as acting on it, so a user
 *    holding `leadRead` alone sees the candidates and no way to merge them.
 *  - `leadConvert` likewise. Converting raises a customer in `internal/ar` and
 *    can happen at most once for a lead, ever.
 *
 * `caseManage` covers the SLA policies and the tenant's weekly business-hours
 * window — the two settings that decide what every case's deadline *means* —
 * plus running the sweep by hand. `caseRead` is enough to look at them.
 */

export const CRM_PERMISSIONS = {
  leadRead: 'crm.lead.read',
  leadCreate: 'crm.lead.create',
  leadUpdate: 'crm.lead.update',
  leadDelete: 'crm.lead.delete',
  /** Retire one lead into another. Irreversible from this app. */
  leadMerge: 'crm.lead.merge',
  /** Raise an `internal/ar` customer from a qualified lead. Once, ever. */
  leadConvert: 'crm.lead.convert',

  opportunityRead: 'crm.opportunity.read',
  opportunityCreate: 'crm.opportunity.create',
  opportunityUpdate: 'crm.opportunity.update',
  opportunityDelete: 'crm.opportunity.delete',

  activityRead: 'crm.activity.read',
  activityCreate: 'crm.activity.create',

  caseRead: 'crm.case.read',
  caseCreate: 'crm.case.create',
  caseUpdate: 'crm.case.update',
  caseAssign: 'crm.case.assign',
  /** Reply to a case, and record its CSAT. */
  caseRespond: 'crm.case.respond',
  caseResolve: 'crm.case.resolve',
  caseClose: 'crm.case.close',
  caseReopen: 'crm.case.reopen',
  /** SLA policies, the weekly business-hours window, and the sweep. */
  caseManage: 'crm.case.manage',

  kbRead: 'crm.kb.read',
  kbManage: 'crm.kb.manage',

  reportRead: 'crm.report.read',

  /* ------------------------------------------------------------------ C5 */

  /**
   * Accounts, contacts and the two small catalogues behind them.
   *
   * An account is not a customer — the two join only through a party — so
   * `crm.account.read` protects the *relationship* record and `ar.customer.read`
   * protects the receivable. Holding one is not holding the other.
   */
  accountRead: 'crm.account.read',
  accountManage: 'crm.account.manage',
  accountDelete: 'crm.account.delete',

  /**
   * Campaigns and segments.
   *
   * `crm.campaign.approve` ⊗ `crm.campaign.send` is a declared
   * segregation-of-duties pair: the content control and the act of sending to
   * forty thousand people are different decisions. The screens never treat
   * holding both as an error — one person may hold both legitimately in a small
   * team — they surface the server's refusal, which is the thing that knows.
   */
  campaignRead: 'crm.campaign.read',
  campaignManage: 'crm.campaign.manage',
  campaignApprove: 'crm.campaign.approve',
  campaignSend: 'crm.campaign.send',

  /** Forecast categories, close reasons, competitors, scoring and routing. */
  forecastManage: 'crm.forecast.manage',

  /** Loyalty is commercial and carries `Module: "crm"` server-side. */
  loyaltyRead: 'crm.loyalty.read',

  /** Read a party to attach an account or a contact to an existing identity. */
  partyRead: 'party.read',
  /** Read the consent summary shown inline on a contact. */
  consentRead: 'party.consent.read',

  /** Read the customer a case or an upsell opportunity belongs to. */
  customerRead: 'ar.customer.read',
  /** Read companies to scope an opportunity or a case. */
  companyRead: 'org.company.read',
} as const

export type CrmPermission = (typeof CRM_PERMISSIONS)[keyof typeof CRM_PERMISSIONS]
